import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CgMore } from 'react-icons/cg';
import { useModal } from '@/hooks';
import { LogModal } from '@/components/modals';
import { useActivityReport } from '@/hooks/app';
import { Flex, LogTable, LogTableColumn } from '@/components/common';
import { envConfig } from '@/configs';
import { useSelfContext } from '@/context';

const PAGE_SIZE = 10;

export interface ActivityReportDesktopProps extends ReturnType<typeof useActivityReport> {
  fromDate: string;
  toDate: string;
}

export function ActivityReportDesktop({
  loading,
  fetchReports,
  items,
  total,
  fromDate,
  toDate
}: ActivityReportDesktopProps) {

  const { findVariantById } = useSelfContext();

  const [t] = useTranslation(['common', 'report']);

  const TABLE_HEADERS = [
    { width: 200, key: 'name', title: t('report:labels.action-type') },
    {
      width: 100,
      key: ['details', 'foodNames', 'value'],
      render: (text) => <div className="w-full flex">
        <span className="truncate w-[200px]">
          {text}
        </span>
      </div>,
      title: t('report:labels.entity-time'),
    },
    {
      key: 'fullDate',
      title: t('common:labels.date'),
      align: 'center',
      render: (text) => <div style={{ direction: 'ltr', textAlign: 'left' }}>{text}</div>
    },
    { key: 'price', title: t('report:activity-report-activity-fields.amount'), align: 'center' },
    { key: 'serveDate', align: 'center', title: t('report:activity-report-activity-fields.serveDate') },
    envConfig.ui.onlySubsidy ? null : {
      key: ['details', 'remainCredit', 'value'],
      title: t('report:activity-report-activity-fields.remainCredit'),
      align: 'center'
    } as any,
    { key: ['action'], title: ' ', align: 'center' }
  ].filter(x => x != null) as LogTableColumn[];

  const modal = useModal();
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    fetchReports({
      page,
      limit: PAGE_SIZE,
      fromDate,
      toDate,
      t,
      findVariantById
    }, true);
  }, [page, findVariantById]);

  useEffect(() => {
    if (page === 0) {
      fetchReports({
        page,
        limit: PAGE_SIZE,
        fromDate,
        toDate,
        t,
        findVariantById
      }, true);
    }
    else {
      setPage(0);
    }
  }, [fromDate, toDate]);


  const rows = items.map(item => ({
    ...item,
    action: (
      <Flex
        inlineFlex
        align="center"
        justify="center"
        onClick={() => modal.show(item.details)}
        className="p-1 rounded-lg cursor-pointer hover:bg-gray-200">
        <CgMore style={{ fontSize: '1.3rem' }} />
      </Flex>
    )
  }));

  return (
    <>
      <LogModal {...modal} />
      <LogTable
        size="small"
        rows={rows}
        className="no-last-th-border"
        columns={TABLE_HEADERS}
        loading={loading}
        pagination={{
          total: total,
          current: page + 1,
          pageSize: PAGE_SIZE,
          onChange: page => setPage(page - 1),
          showSizeChanger: false,
        }}
      />
    </>
  );
}

export default ActivityReportDesktop;
